import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Calendar, Clock, ArrowLeft, ArrowRight, ShieldCheck, Phone, Tag } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo, { SITE_URL, breadcrumbJsonLd } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getPostBySlug, getRelatedPosts } from "@/lib/blogPosts";
import { BUSINESS, SERVICES } from "@/lib/siteData";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function articleJsonLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: [post.cover],
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: BUSINESS.name },
    publisher: {
      "@type": "Organization",
      name: BUSINESS.name,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
    keywords: post.tags.join(", "),
  };
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-10 mb-4">{block.text}</h2>
      );
    case "p":
      return <p className="text-base md:text-[17px] text-foreground/85 leading-relaxed my-4">{block.text}</p>;
    case "ul":
      return (
        <ul className="my-5 space-y-2.5 pl-5 list-disc marker:text-primary text-[17px] text-foreground/85">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="my-5 space-y-2.5 pl-5 list-decimal marker:text-primary marker:font-semibold text-[17px] text-foreground/85">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed pl-1">{it}</li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <aside className="my-8 border-l-4 border-primary bg-secondary px-5 py-5 rounded-r-md">
          {block.title && <p className="font-heading font-semibold text-base mb-1.5">{block.title}</p>}
          <p className="text-[15px] text-foreground/80 leading-relaxed">{block.text}</p>
        </aside>
      );
    case "quote":
      return (
        <blockquote className="my-8 border-l-4 border-border pl-5 italic text-foreground/80">
          {block.text}
        </blockquote>
      );
    case "cta":
      return (
        <div className="my-10 p-7 md:p-8 bg-primary text-primary-foreground rounded-md">
          <p className="font-heading text-lg md:text-xl font-semibold leading-snug">{block.text}</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 h-11 px-5 font-semibold">
              <Link to="/#contact" data-testid="blog-cta-book">Book a free diagnostic <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 h-11 px-5 font-semibold">
              <a href={BUSINESS.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {BUSINESS.phone}</a>
            </Button>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post.slug, 2);
  const relatedService = SERVICES.find((s) => s.key === post.relatedService);

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-background text-foreground">
      <Seo
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover}
        jsonLd={[
          articleJsonLd(post),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary">
          <div className="max-w-3xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-10">
            <Link
              to="/blog"
              data-testid="back-to-blog"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              {post.tags.map((t) => (
                <Badge key={t} variant="outline" className="rounded-full text-[11px] font-medium">
                  <Tag className="h-3 w-3 mr-1" /> {t}
                </Badge>
              ))}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.1] mt-5">
              {post.title}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 leading-relaxed">{post.description}</p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {formatDate(post.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {post.readTime} min read
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-accent" /> Reviewed by Repair Point technicians
              </span>
            </div>
          </div>
        </section>

        {/* Cover */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-4">
          <img
            src={post.cover}
            alt={post.coverAlt}
            className="w-full h-[280px] md:h-[440px] object-cover rounded-md border border-border"
          />
        </div>

        {/* Body */}
        <article data-testid="blog-article-body" className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
          {post.blocks.map((b, i) => <Block key={i} block={b} />)}

          {relatedService && (
            <div className="mt-12 p-6 border border-border rounded-md bg-secondary">
              <p className="text-overline text-primary">Related service</p>
              <p className="font-heading text-lg font-semibold tracking-tight mt-2">
                {relatedService.title} · <span className="text-primary">{relatedService.priceLabel}</span>
              </p>
              <p className="text-sm text-muted-foreground mt-1.5">{relatedService.short}</p>
              <Button asChild className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/#contact">Book this service <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          )}
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="bg-secondary border-t border-border">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Keep reading</h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border rounded-md overflow-hidden">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`related-${p.slug}`}
                    className="group bg-card p-7 md:p-8 hover:bg-card/80 transition-colors block"
                  >
                    <p className="text-[11px] tracking-wider uppercase text-muted-foreground">{p.tags[0]}</p>
                    <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight mt-2 group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-5 group-hover:gap-2.5 transition-all">
                      Read <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
